import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LeftFilters,
  VerticalScroll,
} from '@sorare/core/src/components/search/SearchLayout/ui';
import type { CurrentUser } from '@sorare/core/src/contexts/currentUser';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';
import { MENU } from '@sorare/core/src/lib/filters';
import { FiltersManager } from '@sorare/marketplace/src/search/FiltersManager';
import { BackendSearchBox } from '@sorare/marketplace/src/search/SearchBox';
import { messages } from '@sorare/marketplace/src/searchCards/AdvancedCardSearch/SearchLayout';
import { BackendTotalResults } from '@sorare/marketplace/src/searchCards/BackendTotalResults';

import { BackendRevealableCardRaritySwitcher } from '#src/components/myCards/BackendRevealableCardRaritySwitcher/index.tsx';
import { CardOrCollectionRaritySwitcher } from '#src/components/myCards/CardOrCollectionRaritySwitcher/index.tsx';

const SearchCtn = styled(Vertical).attrs({ gap: 2 })`
  padding: 0 0 var(--double-unit);
`;

type Props = {
  rarity?: Rarity;
  rarityPath?: (rarity: Rarity) => string;
  user: Nullable<CurrentUser>;
  sealed?: boolean;
  onRarityClick?: (rarity: Rarity) => void;
  customFilter?: ReactNode;
  cardFilters: FiltersGroup[];
  showTotalResults?: boolean;
  revealCards?: boolean;
};

export const MyCardsSearchSidebar = ({
  rarity,
  rarityPath,
  user,
  sealed,
  onRarityClick,
  customFilter,
  cardFilters,
  showTotalResults = true,
  revealCards,
}: Props) => {
  const { formatMessage } = useIntl();

  const searchPlaceholder = rarity
    ? messages.rarityCardsPlaceholder
    : messages.defaultPlaceholder;

  const skinGroup = cardFilters.find(g => g.slug === MENU.skin.attribute);
  const otherFilters = cardFilters.filter(g => g.slug !== MENU.skin.attribute);

  return (
    <VerticalScroll>
      <LeftFilters>
        <Vertical gap={2}>
          {!!revealCards && <BackendRevealableCardRaritySwitcher />}
          {!!rarity && !!rarityPath && !skinGroup && !revealCards && (
            <CardOrCollectionRaritySwitcher
              type={rarity}
              rarityPath={rarityPath}
              onClick={onRarityClick}
              user={user}
              sealed={sealed}
            />
          )}
          {customFilter}
          {!!skinGroup && (
            <FiltersManager filters={[skinGroup]} skipClearAllButton />
          )}
          <SearchCtn>
            <BackendSearchBox placeholder={formatMessage(searchPlaceholder)} />
            {!!showTotalResults && <BackendTotalResults />}
          </SearchCtn>
          {otherFilters.length > 0 && (
            <FiltersManager filters={otherFilters} skipClearAllButton />
          )}
        </Vertical>
      </LeftFilters>
    </VerticalScroll>
  );
};
